import React, { useState, useCallback, useMemo } from 'react';
import { Environment } from './art/backgrounds/Environment';
import { ReworkedFish } from './art/fish/ReworkedFish';
import { ReefFish, type ReefFishId } from './art/fish/ReefFish';
import { PremiumPlant } from './art/plants/PremiumPlant';
import { PREMIUM_PLANTS, PREMIUM_PLANT_ORDER, type PremiumPlantId } from './art/plants/premiumPlantData';
import { PremiumDecor } from './art/decor/PremiumDecor';
import { PREMIUM_DECOR, PREMIUM_DECOR_ORDER, type PremiumDecorId } from './art/decor/premiumDecorData';
import { MotionStyles, PV_SCENE_CLASS } from './art/motionStyles';
import { useEnvironmentAnimation } from './hooks';
import { useAnimationLoop, useReducedMotion } from './hooks/useAnimationLoop';
import { useReaction } from './hooks/useReaction';
import type { VisualMode, QualityTier } from './types';
import { QUALITY_CONFIG } from './types';

interface VisualLabProps {
  initialMode?: VisualMode;
  initialQuality?: QualityTier;
}

interface GroundSlot {
  left: string;
  bottom: string;
  width: string;
}

const PLANT_SLOTS: Record<PremiumPlantId, GroundSlot> = {
  ribbon: { left: '2%', bottom: '2%', width: '14%' },
  leafy: { left: '17%', bottom: '0%', width: '15%' },
  coral: { left: '64%', bottom: '3%', width: '13%' },
};

const DECOR_SLOTS: Record<PremiumDecorId, GroundSlot> = {
  lantern: { left: '33%', bottom: '7%', width: '10%' },
  grotto: { left: '43%', bottom: '0%', width: '20%' },
  treasure: { left: '78%', bottom: '4%', width: '14%' },
};

function FocusChip({
  label,
  active,
  dimmed,
  onClick,
}: {
  label: string;
  active: boolean;
  dimmed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 12px',
        borderRadius: '999px',
        border: '1px solid rgba(255,255,255,0.18)',
        background: active ? '#4ecdc4' : 'rgba(255,255,255,0.08)',
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.72rem',
        cursor: 'pointer',
        opacity: dimmed ? 0.55 : 1,
      }}
    >
      {label}
    </button>
  );
}

export function VisualLab({ initialMode = 'idle', initialQuality = 'high' }: VisualLabProps) {
  const [mode, setMode] = useState<VisualMode>(initialMode);
  const [quality, setQuality] = useState<QualityTier>(initialQuality);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const prefersReducedMotion = useReducedMotion();
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);

  const [showFish, setShowFish] = useState(true);
  const [showPlants, setShowPlants] = useState(true);
  const [showDecor, setShowDecor] = useState(true);
  const [focus, setFocus] = useState<string | null>(null);

  const qualityConfig = QUALITY_CONFIG[quality];
  const reduced = reducedMotion || mode === 'reduced';
  // Focus mode slows the ambient motion; the global speed slider scales it further.
  const speed = animationSpeed * (mode === 'focus' ? 0.55 : 1);

  const { reaction, trigger } = useReaction(reduced);

  const envAnimation = useEnvironmentAnimation({
    mode,
    reducedMotion: reduced,
    quality,
    animationSpeed,
  });

  const tick = useCallback(
    (deltaTime: number) => {
      envAnimation.tick(deltaTime);
    },
    [envAnimation]
  );

  useAnimationLoop({
    mode,
    reducedMotion: reduced,
    quality,
    animationSpeed,
    onTick: tick,
    enabled: true,
  });

  const containerStyle: React.CSSProperties = useMemo(
    () => ({
      position: 'relative',
      width: '100%',
      maxWidth: 1000,
      aspectRatio: '3 / 2',
      borderRadius: '24px',
      overflow: 'hidden',
      boxShadow: '0 24px 80px rgba(10, 40, 40, 0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.08)',
    }),
    []
  );

  const aquariumWidth = 1000;
  const aquariumHeight = 667;

  const toggleFocus = (id: string) => setFocus((current) => (current === id ? null : id));
  const assetState = (id: string) => ({
    active: focus === id,
    dimmed: focus !== null && focus !== id,
  });
  const heroState = assetState('fish:hero');
  const moorishState = assetState('fish:moorish-idol');
  const clownfishState = assetState('fish:clownfish');
  const reefIds: ReefFishId[] = ['flame-angelfish','royal-gramma','clown-triggerfish'];

  const chipRowStyle: React.CSSProperties = { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center', padding: '24px', minHeight: '100vh', background: '#0a1a1a' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', width: '100%', maxWidth: 1000 }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
          <h1 style={{ margin: 0, color: '#fff', fontSize: '1.5rem', fontWeight: 700, textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
            ThriveCove Visual Lab
          </h1>
          <span
            style={{
              padding: '4px 12px',
              borderRadius: '999px',
              background: mode === 'idle' ? '#4ecdc4' : mode === 'focus' ? '#5aa3e0' : '#888',
              color: '#fff',
              fontSize: '0.7rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {mode.toUpperCase()}
          </span>
          <span
            style={{
              padding: '4px 12px',
              borderRadius: '999px',
              background: quality === 'high' ? '#4ecdc4' : quality === 'balanced' ? '#8a8' : '#a84',
              color: '#fff',
              fontSize: '0.7rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {quality.toUpperCase()}
          </span>
          {reduced && (
            <span
              style={{
                padding: '4px 12px',
                borderRadius: '999px',
                background: '#a85cf0',
                color: '#fff',
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Reduced Motion
            </span>
          )}
        </div>

        <div className={`${PV_SCENE_CLASS} ${reduced ? 'pv-reduced' : ''} ${quality === 'low' ? 'pv-quiet' : ''}`} style={containerStyle}>
          <MotionStyles />
          <Environment
            animationState={envAnimation.state}
            quality={quality}
            reducedMotion={reduced}
            width={aquariumWidth}
            height={aquariumHeight}
          />

          {/* Decorations sit behind the plants and fish. */}
          {showDecor && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {PREMIUM_DECOR_ORDER.map((id) => {
                const slot = DECOR_SLOTS[id];
                const state = assetState(`decor:${id}`);
                return (
                  <div key={id} style={{ position: 'absolute', left: slot.left, bottom: slot.bottom, width: slot.width }}>
                    <PremiumDecor
                      variant={id}
                      reducedMotion={reduced}
                      quality={quality}
                      speed={speed}
                      active={state.active}
                      dimmed={state.dimmed}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Plants along the substrate. */}
          {showPlants && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {PREMIUM_PLANT_ORDER.map((id) => {
                const slot = PLANT_SLOTS[id];
                const state = assetState(`plant:${id}`);
                return (
                  <div key={id} style={{ position: 'absolute', left: slot.left, bottom: slot.bottom, width: slot.width }}>
                    <PremiumPlant
                      variant={id}
                      reducedMotion={reduced}
                      quality={quality}
                      speed={speed}
                      active={state.active}
                      dimmed={state.dimmed}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Only approved image-based fish are shown in the cove. */}
          {showFish && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              <div style={{ position: 'absolute', left: 0, top: '27%', width: '100%', height: '25%', zIndex: 3 }}>
                <ReworkedFish id="hero-orange" reducedMotion={reduced} reaction={reaction} speed={speed}
                  active={heroState.active} dimmed={heroState.dimmed} />
              </div>

              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: '46%',
                  width: '100%',
                  height: '28%',
                  zIndex: 4,
                }}
              >
                <ReworkedFish
                  id="moorish-idol"
                  reaction={reaction}
                  reducedMotion={reduced}
                  speed={speed}
                  active={moorishState.active}
                  dimmed={moorishState.dimmed}
                />
              </div>

              {reefIds.map((id, i) => (
                <div key={id} style={{ position:'absolute',left:0,top:`${57+i*12}%`,width:'100%',height:'19%',zIndex:6+i }}>
                  <ReefFish id={id} reaction={reaction} reducedMotion={reduced} speed={speed}
                    active={focus===`fish:${id}`} dimmed={focus!==null && focus!==`fish:${id}`} />
                </div>
              ))}

              <div style={{ position: 'absolute', left: 0, top: '11%', width: '100%', height: '23%', zIndex: 5 }}>
                <ReworkedFish
                  id="clownfish"
                  reaction={reaction}
                  reducedMotion={reduced}
                  speed={speed}
                  active={clownfishState.active}
                  dimmed={clownfishState.dimmed}
                />
              </div>
            </div>
          )}
        </div>

        {showControls && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              width: '100%',
              maxWidth: 1000,
              padding: '20px',
              background: 'rgba(20, 40, 40, 0.85)',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <strong style={{ color: '#fff', fontSize: '0.9rem', minWidth: '100px' }}>Mode:</strong>
              {(['idle', 'focus', 'reduced'] as VisualMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: mode === m ? '#4ecdc4' : 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <strong style={{ color: '#fff', fontSize: '0.9rem', minWidth: '100px' }}>Quality:</strong>
              {(['low', 'balanced', 'high'] as QualityTier[]).map((q) => (
                <button
                  key={q}
                  onClick={() => setQuality(q)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: quality === q ? '#4ecdc4' : 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {q.charAt(0).toUpperCase() + q.slice(1)}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <strong style={{ color: '#fff', fontSize: '0.9rem', minWidth: '100px' }}>Reduced Motion:</strong>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={reducedMotion}
                  onChange={(e) => setReducedMotion(e.target.checked)}
                  style={{ width: '18px', height: '18px', accentColor: '#4ecdc4' }}
                />
                <span style={{ fontSize: '0.85rem' }}>{reducedMotion ? 'ON' : 'OFF'}</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <strong style={{ color: '#fff', fontSize: '0.9rem', minWidth: '100px' }}>Speed:</strong>
              <input
                type="range"
                min="0.1"
                max="3"
                step="0.1"
                value={animationSpeed}
                onChange={(e) => setAnimationSpeed(Number(e.target.value))}
                style={{ flex: 1, minWidth: '150px', accentColor: '#4ecdc4' }}
              />
              <span style={{ color: '#fff', fontSize: '0.85rem', minWidth: '40px', textAlign: 'right' }}>
                {animationSpeed.toFixed(1)}x
              </span>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <strong style={{ color: '#fff', fontSize: '0.9rem', minWidth: '100px' }}>Reactions:</strong>
              <button
                onClick={() => trigger('happy')}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#ff6b4a', color: '#fff', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
              >
                Happy
              </button>
              <button
                onClick={() => trigger('feeding')}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#4ecdc4', color: '#fff', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
              >
                Feeding
              </button>
              <button
                onClick={() => trigger('discovery')}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#a85cf0', color: '#fff', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
              >
                Discovery
              </button>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <strong style={{ color: '#fff', fontSize: '0.9rem', minWidth: '100px' }}>Show:</strong>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', cursor: 'pointer' }}>
                <input type="checkbox" checked={showFish} onChange={(e) => setShowFish(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: '#4ecdc4' }} />
                <span style={{ fontSize: '0.85rem' }}>Fish</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', cursor: 'pointer' }}>
                <input type="checkbox" checked={showPlants} onChange={(e) => setShowPlants(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: '#4ecdc4' }} />
                <span style={{ fontSize: '0.85rem' }}>Plants</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', cursor: 'pointer' }}>
                <input type="checkbox" checked={showDecor} onChange={(e) => setShowDecor(e.target.checked)} style={{ width: '18px', height: '18px', accentColor: '#4ecdc4' }} />
                <span style={{ fontSize: '0.85rem' }}>Decor</span>
              </label>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <strong style={{ color: '#fff', fontSize: '0.85rem', minWidth: '100px' }}>Fish:</strong>
                <div style={chipRowStyle}>
                  <FocusChip
                    label="Hero Orange"
                    active={focus === 'fish:hero'}
                    dimmed={focus !== null && focus !== 'fish:hero'}
                    onClick={() => toggleFocus('fish:hero')}
                  />
                  <FocusChip
                    label="Moorish Idol"
                    active={focus === 'fish:moorish-idol'}
                    dimmed={focus !== null && focus !== 'fish:moorish-idol'}
                    onClick={() => toggleFocus('fish:moorish-idol')}
                  />
                  {reefIds.map((id) => (
                    <FocusChip key={id} label={{'flame-angelfish':'Flame Angelfish','royal-gramma':'Royal Gramma','clown-triggerfish':'Clown Triggerfish'}[id]}
                      active={focus===`fish:${id}`} dimmed={focus!==null && focus!==`fish:${id}`} onClick={() => toggleFocus(`fish:${id}`)} />
                  ))}
                  <FocusChip
                    label="Clownfish"
                    active={focus === 'fish:clownfish'}
                    dimmed={focus !== null && focus !== 'fish:clownfish'}
                    onClick={() => toggleFocus('fish:clownfish')}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <strong style={{ color: '#fff', fontSize: '0.85rem', minWidth: '100px' }}>Plants:</strong>
                <div style={chipRowStyle}>
                  {PREMIUM_PLANT_ORDER.map((id) => (
                    <FocusChip
                      key={id}
                      label={PREMIUM_PLANTS[id].label}
                      active={focus === `plant:${id}`}
                      dimmed={focus !== null && focus !== `plant:${id}`}
                      onClick={() => toggleFocus(`plant:${id}`)}
                    />
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <strong style={{ color: '#fff', fontSize: '0.85rem', minWidth: '100px' }}>Decor:</strong>
                <div style={chipRowStyle}>
                  {PREMIUM_DECOR_ORDER.map((id) => (
                    <FocusChip
                      key={id}
                      label={PREMIUM_DECOR[id].label}
                      active={focus === `decor:${id}`}
                      dimmed={focus !== null && focus !== `decor:${id}`}
                      onClick={() => toggleFocus(`decor:${id}`)}
                    />
                  ))}
                  {focus !== null && (
                    <button
                      onClick={() => setFocus(null)}
                      style={{ padding: '6px 12px', borderRadius: '999px', border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showControls}
                  onChange={(e) => setShowControls(e.target.checked)}
                  style={{ width: '18px', height: '18px', accentColor: '#4ecdc4' }}
                />
                <span style={{ fontSize: '0.85rem' }}>Show Controls</span>
              </label>
              {qualityConfig.enableParticles ? null : <span style={{ color: '#8ad0d0', fontSize: '0.75rem' }}>Low power: particles and secondary flourishes off</span>}
            </div>
          </div>
        )}

        {!showControls && (
          <button
            onClick={() => setShowControls(true)}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: '#4ecdc4',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(78, 205, 196, 0.4)',
            }}
          >
            Show Controls
          </button>
        )}

        <div
          style={{
            marginTop: '8px',
            padding: '12px 16px',
            background: 'rgba(20, 40, 40, 0.6)',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.05)',
            fontSize: '0.75rem',
            color: '#8ad0d0',
            textAlign: 'center',
            maxWidth: 1000,
          }}
        >
          <strong>Visual Lab Prototype</strong> - Development only. All artwork is original ThriveCove IP.
          No copyrighted characters or designs. Uses React + SVG + CSS transforms only.
        </div>
      </div>
    </div>
  );
}
